import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useApplications } from '../../context/ApplicationContext';
import { useWorkflow } from '../../context/WorkflowContext';
import { useNotifications } from '../../context/NotificationContext';
import AdminLayout from '../../components/admin/AdminLayout';
import Card, { CardHeader, CardBody } from '../../components/Card';
import Button from '../../components/Button';
import { UsersIcon, FileTextIcon, CreditCardIcon, BuildingIcon, BarChartIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ChevronRightIcon, CalendarIcon, DollarSignIcon, BriefcaseIcon, UserCheckIcon, ArrowUpRightIcon, ArrowDownRightIcon, BellIcon, BarChart4Icon, TagIcon } from 'lucide-react';
const AdminDashboard: React.FC = () => {
  const {
    admin
  } = useAdminAuth();
  const {
    applications,
    payments,
    getApplicationStats,
    getPaymentStats,
    getRecentApplications,
    getRecentPayments,
    getApplicationsByMdaId
  } = useApplications();
  const {
    getAssignedWorkflowSteps,
    getPendingEscalations
  } = useWorkflow();
  const {
    unreadCount
  } = useNotifications();
  const [stats, setStats] = useState({
    applications: {
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      processing: 0
    },
    payments: {
      total: 0,
      successful: 0,
      pending: 0,
      failed: 0,
      totalAmount: 0
    }
  });
  const [recentApplications, setRecentApplications] = useState<any[]>([]);
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [assignedWorkflows, setAssignedWorkflows] = useState<any[]>([]);
  const [pendingEscalations, setPendingEscalations] = useState<any[]>([]);
  const [mdaApplications, setMdaApplications] = useState<any[]>([]);
  useEffect(() => {
    // Get application and payment stats
    setStats({
      applications: getApplicationStats(),
      payments: getPaymentStats()
    });
    // Get recent applications and payments
    setRecentApplications(getRecentApplications(5));
    setRecentPayments(getRecentPayments(5));
    // Get assigned workflow steps
    setAssignedWorkflows(getAssignedWorkflowSteps());
    // Get pending escalations
    setPendingEscalations(getPendingEscalations());
    // Get applications for this MDA if admin has mdaId
    if (admin?.mdaId) {
      setMdaApplications(getApplicationsByMdaId(admin.mdaId));
    }
  }, [admin]);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1441">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-1442" />
            Approved
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1443">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-1444" />
            Rejected
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1445">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-1446" />
            Pending
          </span>;
      case 'processing':
      case 'under_review':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1447">
            <BarChart4Icon className="w-3 h-3 mr-1" data-id="element-1448" />
            Processing
          </span>;
      case 'awaiting_payment':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1449">
            <CreditCardIcon className="w-3 h-3 mr-1" data-id="element-1450" />
            Awaiting Payment
          </span>;
      case 'awaiting_documents':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1451">
            <FileTextIcon className="w-3 h-3 mr-1" data-id="element-1452" />
            Awaiting Documents
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1453">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1454" />
            {status.replace('_', ' ').toUpperCase()}
          </span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };
  // Calculate percentage change for stats (mock data for demo)
  const getPercentageChange = (metric: string) => {
    const changes = {
      applications: 12.5,
      approved: 8.3,
      rejected: -4.2,
      payments: 15.7,
      amount: 22.4
    };
    return changes[metric as keyof typeof changes] || 0;
  };
  return <AdminLayout data-id="element-1455">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-1456">
        <div className="max-w-7xl mx-auto" data-id="element-1457">
          {/* Header */}
          <div className="mb-6" data-id="element-1458">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1459">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1460">
              Welcome back, {admin?.name || 'Admin'}. Here's an overview of the
              portal's activity.
            </p>
          </div>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6" data-id="element-1461">
            <Card className="bg-white" data-id="element-1462">
              <CardBody className="p-5" data-id="element-1463">
                <div className="flex justify-between items-start" data-id="element-1464">
                  <div data-id="element-1465">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1466">
                      Total Applications
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-1467">
                      {stats.applications.total}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-full" data-id="element-1468">
                    <FileTextIcon className="h-6 w-6 text-blue-500" data-id="element-1469" />
                  </div>
                </div>
                <div className="mt-4 flex items-center" data-id="element-1470">
                  <span className={`text-xs font-medium ${getPercentageChange('applications') >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center`} data-id="element-1471">
                    {getPercentageChange('applications') >= 0 ? <ArrowUpRightIcon className="h-3 w-3 mr-1" data-id="element-1472" /> : <ArrowDownRightIcon className="h-3 w-3 mr-1" data-id="element-1473" />}
                    {Math.abs(getPercentageChange('applications'))}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1.5" data-id="element-1474">
                    from last month
                  </span>
                </div>
              </CardBody>
            </Card>
            <Card className="bg-white" data-id="element-1475">
              <CardBody className="p-5" data-id="element-1476">
                <div className="flex justify-between items-start" data-id="element-1477">
                  <div data-id="element-1478">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1479">
                      Pending Applications
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-1480">
                      {stats.applications.pending}
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-full" data-id="element-1481">
                    <ClockIcon className="h-6 w-6 text-yellow-500" data-id="element-1482" />
                  </div>
                </div>
                <div className="mt-4 flex items-center" data-id="element-1483">
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1484">
                    <div className="bg-yellow-500 h-2.5 rounded-full" style={{
                    width: `${stats.applications.total ? stats.applications.pending / stats.applications.total * 100 : 0}%`
                  }} data-id="element-1485"></div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className="bg-white" data-id="element-1486">
              <CardBody className="p-5" data-id="element-1487">
                <div className="flex justify-between items-start" data-id="element-1488">
                  <div data-id="element-1489">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1490">
                      Total Revenue
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-1491">
                      {formatCurrency(stats.payments.totalAmount)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-full" data-id="element-1492">
                    <DollarSignIcon className="h-6 w-6 text-green-500" data-id="element-1493" />
                  </div>
                </div>
                <div className="mt-4 flex items-center" data-id="element-1494">
                  <span className={`text-xs font-medium ${getPercentageChange('amount') >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center`} data-id="element-1495">
                    {getPercentageChange('amount') >= 0 ? <ArrowUpRightIcon className="h-3 w-3 mr-1" data-id="element-1496" /> : <ArrowDownRightIcon className="h-3 w-3 mr-1" data-id="element-1497" />}
                    {Math.abs(getPercentageChange('amount'))}%
                  </span>
                  <span className="text-xs text-gray-500 ml-1.5" data-id="element-1498">
                    from last month
                  </span>
                </div>
              </CardBody>
            </Card>
            <Card className="bg-white" data-id="element-1499">
              <CardBody className="p-5" data-id="element-1500">
                <div className="flex justify-between items-start" data-id="element-1501">
                  <div data-id="element-1502">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1503">
                      Approval Rate
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-1504">
                      {stats.applications.total ? Math.round(stats.applications.approved / stats.applications.total * 100) : 0}
                      %
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-full" data-id="element-1505">
                    <CheckCircleIcon className="h-6 w-6 text-purple-500" data-id="element-1506" />
                  </div>
                </div>
                <div className="mt-4 flex items-center" data-id="element-1507">
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1508">
                    <div className="bg-purple-500 h-2.5 rounded-full" style={{
                    width: `${stats.applications.total ? stats.applications.approved / stats.applications.total * 100 : 0}%`
                  }} data-id="element-1509"></div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-1510">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6" data-id="element-1511">
              {/* Pending Approvals */}
              <Card data-id="element-1512">
                <CardHeader className="bg-gray-50 flex items-center justify-between" data-id="element-1513">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-1514">
                    Pending Approvals
                  </h2>
                  <Link to="/admin/applications" className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-1515">
                    View All
                    <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-1516" />
                  </Link>
                </CardHeader>
                <CardBody className="p-0" data-id="element-1517">
                  {assignedWorkflows.length === 0 ? <div className="p-4 text-center" data-id="element-1518">
                      <p className="text-gray-500" data-id="element-1519">
                        No applications pending your approval.
                      </p>
                    </div> : <div className="divide-y divide-gray-200" data-id="element-1520">
                      {assignedWorkflows.map(item => <div key={item.instance.id} className="p-4 hover:bg-gray-50 transition-colors" data-id="element-1521">
                          <div className="flex items-start justify-between" data-id="element-1522">
                            <div data-id="element-1523">
                              <h3 className="text-sm font-medium text-gray-900" data-id="element-1524">
                                {item.application.serviceName}
                              </h3>
                              <div className="flex items-center mt-1 space-x-2" data-id="element-1525">
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1526">
                                  <TagIcon className="h-3 w-3 mr-1" data-id="element-1527" />
                                  {item.application.referenceNumber}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1528">
                                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1529" />
                                  {formatDate(item.application.submittedAt)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-1530">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1531">
                                <ClockIcon className="w-3 h-3 mr-1" data-id="element-1532" />
                                Awaiting Approval
                              </span>
                            </div>
                          </div>
                          <div className="mt-3" data-id="element-1533">
                            <p className="text-xs text-gray-600" data-id="element-1534">
                              <span className="font-medium" data-id="element-1535">Current Step:</span>{' '}
                              {item.step.name}
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-1536">
                              <span className="font-medium" data-id="element-1537">
                                Assigned Role:
                              </span>{' '}
                              {item.step.assignedRoleName}
                            </p>
                          </div>
                          <div className="mt-3 flex justify-end space-x-2" data-id="element-1538">
                            <Button variant="outline" size="sm" onClick={() => {
                        // Navigate to application detail page
                      }} data-id="element-1539">
                              View Details
                            </Button>
                            <Button variant="primary" size="sm" onClick={() => {
                        // Open approval dialog
                      }} data-id="element-1540">
                              Process Approval
                            </Button>
                          </div>
                        </div>)}
                    </div>}
                </CardBody>
              </Card>
              {/* SLA Escalations */}
              {pendingEscalations.length > 0 && <Card className="border-l-4 border-red-500" data-id="element-1541">
                  <CardHeader className="bg-red-50 flex items-center justify-between" data-id="element-1542">
                    <div className="flex items-center" data-id="element-1543">
                      <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-1544" />
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-1545">
                        SLA Escalations
                      </h2>
                    </div>
                    <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full" data-id="element-1546">
                      {pendingEscalations.length} Pending
                    </span>
                  </CardHeader>
                  <CardBody className="p-0" data-id="element-1547">
                    <div className="divide-y divide-gray-200" data-id="element-1548">
                      {pendingEscalations.map(item => <div key={item.instance.id} className="p-4 hover:bg-gray-50 transition-colors" data-id="element-1549">
                          <div className="flex items-start justify-between" data-id="element-1550">
                            <div data-id="element-1551">
                              <h3 className="text-sm font-medium text-gray-900" data-id="element-1552">
                                {item.application.serviceName}
                              </h3>
                              <div className="flex items-center mt-1 space-x-2" data-id="element-1553">
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1554">
                                  <TagIcon className="h-3 w-3 mr-1" data-id="element-1555" />
                                  {item.application.referenceNumber}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1556">
                                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1557" />
                                  {formatDate(item.application.submittedAt)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-1558">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1559">
                                <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1560" />
                                SLA Exceeded
                              </span>
                            </div>
                          </div>
                          <div className="mt-3" data-id="element-1561">
                            <p className="text-xs text-gray-600" data-id="element-1562">
                              <span className="font-medium" data-id="element-1563">Current Step:</span>{' '}
                              {item.step.name}
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-1564">
                              <span className="font-medium" data-id="element-1565">
                                Assigned Role:
                              </span>{' '}
                              {item.step.assignedRoleName}
                            </p>
                            <p className="text-xs text-red-600 mt-1" data-id="element-1566">
                              <span className="font-medium" data-id="element-1567">SLA:</span>{' '}
                              {item.step.sla} hours (exceeded)
                            </p>
                          </div>
                          <div className="mt-3 flex justify-end space-x-2" data-id="element-1568">
                            <Button variant="outline" size="sm" onClick={() => {
                        // Navigate to application detail page
                      }} data-id="element-1569">
                              View Details
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => {
                        // Open escalation dialog
                      }} data-id="element-1570">
                              Handle Escalation
                            </Button>
                          </div>
                        </div>)}
                    </div>
                  </CardBody>
                </Card>}
              {/* Recent Applications */}
              <Card data-id="element-1571">
                <CardHeader className="bg-gray-50 flex items-center justify-between" data-id="element-1572">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-1573">
                    Recent Applications
                  </h2>
                  <Link to="/admin/applications" className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-1574">
                    View All
                    <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-1575" />
                  </Link>
                </CardHeader>
                <CardBody className="p-0" data-id="element-1576">
                  {recentApplications.length === 0 ? <div className="p-4 text-center" data-id="element-1577">
                      <p className="text-gray-500" data-id="element-1578">
                        No applications have been submitted yet.
                      </p>
                    </div> : <div className="divide-y divide-gray-200" data-id="element-1579">
                      {recentApplications.map(application => <div key={application.id} className="p-4 hover:bg-gray-50 transition-colors" data-id="element-1580">
                          <div className="flex items-center justify-between" data-id="element-1581">
                            <div data-id="element-1582">
                              <h3 className="text-sm font-medium text-gray-900" data-id="element-1583">
                                {application.serviceName}
                              </h3>
                              <div className="flex items-center mt-1 space-x-2" data-id="element-1584">
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1585">
                                  <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-1586" />
                                  {application.userName}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1587">
                                  <TagIcon className="h-3 w-3 mr-1" data-id="element-1588" />
                                  {application.referenceNumber}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1589">
                                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1590" />
                                  {formatDate(application.submittedAt)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-1591">
                              {getStatusBadge(application.status)}
                              {application.isPaid ? <span className="mt-1 text-xs text-green-600 flex items-center" data-id="element-1592">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1593" />
                                  Paid
                                </span> : <span className="mt-1 text-xs text-red-600 flex items-center" data-id="element-1594">
                                  <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-1595" />
                                  Unpaid
                                </span>}
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </CardBody>
              </Card>
            </div>
            {/* Right Column */}
            <div className="space-y-6" data-id="element-1596">
              {/* Quick Stats */}
              <Card data-id="element-1597">
                <CardHeader className="bg-gray-50" data-id="element-1598">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-1599">
                    Application Status
                  </h2>
                </CardHeader>
                <CardBody className="p-4" data-id="element-1600">
                  <div className="space-y-4" data-id="element-1601">
                    <div data-id="element-1602">
                      <div className="flex justify-between items-center mb-1" data-id="element-1603">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1604">
                          Pending
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-1605">
                          {stats.applications.pending} (
                          {stats.applications.total ? Math.round(stats.applications.pending / stats.applications.total * 100) : 0}
                          %)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1606">
                        <div className="bg-yellow-500 h-2.5 rounded-full" style={{
                        width: `${stats.applications.total ? stats.applications.pending / stats.applications.total * 100 : 0}%`
                      }} data-id="element-1607"></div>
                      </div>
                    </div>
                    <div data-id="element-1608">
                      <div className="flex justify-between items-center mb-1" data-id="element-1609">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1610">
                          Processing
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-1611">
                          {stats.applications.processing} (
                          {stats.applications.total ? Math.round(stats.applications.processing / stats.applications.total * 100) : 0}
                          %)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1612">
                        <div className="bg-blue-500 h-2.5 rounded-full" style={{
                        width: `${stats.applications.total ? stats.applications.processing / stats.applications.total * 100 : 0}%`
                      }} data-id="element-1613"></div>
                      </div>
                    </div>
                    <div data-id="element-1614">
                      <div className="flex justify-between items-center mb-1" data-id="element-1615">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1616">
                          Approved
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-1617">
                          {stats.applications.approved} (
                          {stats.applications.total ? Math.round(stats.applications.approved / stats.applications.total * 100) : 0}
                          %)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1618">
                        <div className="bg-green-500 h-2.5 rounded-full" style={{
                        width: `${stats.applications.total ? stats.applications.approved / stats.applications.total * 100 : 0}%`
                      }} data-id="element-1619"></div>
                      </div>
                    </div>
                    <div data-id="element-1620">
                      <div className="flex justify-between items-center mb-1" data-id="element-1621">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1622">
                          Rejected
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-1623">
                          {stats.applications.rejected} (
                          {stats.applications.total ? Math.round(stats.applications.rejected / stats.applications.total * 100) : 0}
                          %)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1624">
                        <div className="bg-red-500 h-2.5 rounded-full" style={{
                        width: `${stats.applications.total ? stats.applications.rejected / stats.applications.total * 100 : 0}%`
                      }} data-id="element-1625"></div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
              {/* Recent Payments */}
              <Card data-id="element-1626">
                <CardHeader className="bg-gray-50 flex items-center justify-between" data-id="element-1627">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-1628">
                    Recent Payments
                  </h2>
                  <Link to="/admin/payments" className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-1629">
                    View All
                    <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-1630" />
                  </Link>
                </CardHeader>
                <CardBody className="p-0" data-id="element-1631">
                  {recentPayments.length === 0 ? <div className="p-4 text-center" data-id="element-1632">
                      <p className="text-gray-500" data-id="element-1633">
                        No payments have been made yet.
                      </p>
                    </div> : <div className="divide-y divide-gray-200" data-id="element-1634">
                      {recentPayments.map(payment => <div key={payment.id} className="p-4 hover:bg-gray-50 transition-colors" data-id="element-1635">
                          <div className="flex items-start justify-between" data-id="element-1636">
                            <div data-id="element-1637">
                              <h3 className="text-sm font-medium text-gray-900" data-id="element-1638">
                                {payment.serviceName}
                              </h3>
                              <div className="flex items-center mt-1 space-x-2" data-id="element-1639">
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1640">
                                  <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-1641" />
                                  {payment.userName}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center" data-id="element-1642">
                                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1643" />
                                  {formatDate(payment.date)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-1644">
                              <span className="text-sm font-medium text-gray-900" data-id="element-1645">
                                {formatCurrency(payment.amount)}
                              </span>
                              <span className={`mt-1 text-xs ${payment.status === 'successful' ? 'text-green-600' : payment.status === 'failed' ? 'text-red-600' : 'text-yellow-600'} flex items-center`} data-id="element-1646">
                                {payment.status === 'successful' ? <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1647" /> : payment.status === 'failed' ? <XCircleIcon className="h-3 w-3 mr-1" data-id="element-1648" /> : <ClockIcon className="h-3 w-3 mr-1" data-id="element-1649" />}
                                {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </CardBody>
              </Card>
              {/* Quick Links */}
              <Card data-id="element-1650">
                <CardHeader className="bg-gray-50" data-id="element-1651">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-1652">
                    Quick Links
                  </h2>
                </CardHeader>
                <CardBody className="p-4" data-id="element-1653">
                  <div className="grid grid-cols-2 gap-3" data-id="element-1654">
                    <Link to="/admin/applications" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1655">
                      <FileTextIcon className="h-6 w-6 text-blue-500 mb-2" data-id="element-1656" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1657">
                        Applications
                      </span>
                    </Link>
                    <Link to="/admin/users" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1658">
                      <UsersIcon className="h-6 w-6 text-indigo-500 mb-2" data-id="element-1659" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1660">
                        Users
                      </span>
                    </Link>
                    <Link to="/admin/payments" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1661">
                      <CreditCardIcon className="h-6 w-6 text-green-500 mb-2" data-id="element-1662" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1663">
                        Payments
                      </span>
                    </Link>
                    <Link to="/admin/reports" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1664">
                      <BarChartIcon className="h-6 w-6 text-purple-500 mb-2" data-id="element-1665" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1666">
                        Reports
                      </span>
                    </Link>
                    <Link to="/admin/notifications" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1667">
                      <div className="relative" data-id="element-1668">
                        <BellIcon className="h-6 w-6 text-amber-500 mb-2" data-id="element-1669" />
                        {unreadCount > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs" data-id="element-1670">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>}
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-1671">
                        Notifications
                      </span>
                    </Link>
                    <Link to="/admin/settings" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center" data-id="element-1672">
                      <BriefcaseIcon className="h-6 w-6 text-gray-500 mb-2" data-id="element-1673" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1674">
                        Settings
                      </span>
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>;
};
export default AdminDashboard;